import './LegacyAlert.scss';

import React, { ReactNode, SetStateAction, createContext, useCallback, useEffect, useState } from 'react';

import Alert from 'react-bootstrap/Alert';
import { BEM } from '@feedfm/feed-util';
import { ConfirmDialog } from '~/components/ConfirmDialog/ConfirmDialog';
import { ErrorDisplay } from '~/components/ErrorDisplay/ErrorDisplay';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Variant } from 'react-bootstrap/esm/types';
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { isEqual } from 'lodash';
import { useLocation } from 'react-router-dom';

/**
 * Provide a global alert that everybody can use with this simple pattern:
 *
 * const { useAlert } = require('./Alert');
 *
 * const addAlert = useAlert();
 *
 * addAlert('this is a an alert!', 'info');
 * addAlert('this is a an alert!', 'error');
 * addAlert('this is a an alert!', 'warning');
 * addAlert('this is a an alert!', 'success');
 *
 * You can also save an alert to local storage for use on the next page load:
 *
 * const { saveFlashAlert } = require('./Alert');
 *
 * saveFlashAlert('this is a flash alert!', 'info');
 *
 * (note that isn't a react component - it just stores a cookie)
 *
 * Also provide a simple way to display a modal on a page:
 *
 * const { useModal } = require('./Alert');
 *
 * const showModal = useModal();
 *
 * .. then, in an event handler:
 *
    showModal((onHide) => (
        <Modal
          show={true}
          onHide={onHide}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Title!
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
  
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant='primary'
              onClick={onHide}
            >
              Hide
            </Button>
          </Modal.Footer>
        </Modal>
    ))
 *
 *
 */

const DEFAULT_TIMEOUT = 3000;

const bem = new BEM( 'ops-alert', [] );

export const AlertContext = createContext<AlertContextInterface>( {
  addAlert: () => console.log( 'adding alert with no context' ),
  showModal: () => console.log( 'showing modal with no context' ),
} );

const FLASH_ALERT_KEY = 'flash';

export function saveFlashAlert( message: string, variant: Variant, autoDismiss = true ) {
  try {
    sessionStorage.setItem( FLASH_ALERT_KEY, JSON.stringify( [message, variant, autoDismiss] ) );
  } catch ( error ) {
    // *shrug* nothing to do here
  }
}

function retrieveFlashAlert(): [string?, Variant?, boolean?] | null {
  try {
    const storedJson = sessionStorage.getItem( FLASH_ALERT_KEY );

    if ( !storedJson ) {
      return null;
    }
    return JSON.parse( storedJson );
  } catch ( e ) {
    return [];
  }
}

function deleteFlashAlert() {
  try {
    sessionStorage.removeItem( FLASH_ALERT_KEY );
  } catch ( e ) {
    // nada
  }
}

let id = 0;

interface AlertObject {
  id: number;
  message: ExtendedAlertMessage;
  variant: Variant;
  dismissTime?: number; // Date in Epoch milliseconds
}

/**
 * Instead of passing a simple string, you can pass an object with a `message` property,
 * and optionally an `error` property (which will be rendered as an ErrorDisplay) and a
 * `link` property (which will be rendered as a clickable link).
 */
export interface ExtendedAlertMessage {
  message: string;
  error?: any;
  link?: string;
}

export interface AlertContextInterface {
  addAlert:
  | ( ( message: string | ExtendedAlertMessage, variant: Variant, autoDismiss?: boolean, seconds?: number ) => void )
  | null;
  showModal: ( ( arg0: ( onHide: () => void ) => ReactNode ) => void ) | null;
}

type SetContext = ( arg0: SetStateAction<AlertContextInterface> ) => void;

interface DisplayAlertProps {
  setContext: SetContext;
  defaultTimeout: number;
}

export const DisplayAlert = ( { setContext, defaultTimeout }: DisplayAlertProps ) => {
  const [alerts, setAlerts] = useState<AlertObject[]>( [] );
  const [dismissTimeout, setDismissTimeout] = useState<unknown>( null );
  const [showMoreDialog, setShowMoreDialog] = useState<AlertObject | null>( null );

  const addAlert = useCallback(
    ( message: string | ExtendedAlertMessage, variant: Variant, autoDismiss: boolean = true, seconds?: number ) => {
      if ( typeof message === 'string' ) {
        message = { message };
      }

      const alert: AlertObject = {
        id: id++,
        message,
        variant,
      };

      if ( autoDismiss ) {
        const onDismiss = () => {
          setAlerts( ( previousAlerts ) => {
            previousAlerts = previousAlerts.filter(
              ( previousAlert ) => !previousAlert.dismissTime || previousAlert.dismissTime > Date.now() + 1000,
            );

            const nextDismissTime = previousAlerts.reduce(
              ( dismissTime, previousAlert ) =>
                previousAlert.dismissTime && ( !dismissTime || previousAlert.dismissTime < dismissTime )
                  ? previousAlert.dismissTime
                  : dismissTime,
              0,
            );

            if ( nextDismissTime ) {
              setDismissTimeout( setTimeout( onDismiss, Math.abs( nextDismissTime - Date.now() ) ) );
            } else {
              setDismissTimeout( null );
            }

            return previousAlerts;
          } );
        };

        alert.dismissTime = Date.now() + ( seconds ? seconds * 1000 : defaultTimeout );

        // find when next alert should go away
        const nextDismissTime = alerts.reduce(
          ( dismissTime, currentAlert ) =>
            currentAlert.dismissTime && currentAlert.dismissTime < dismissTime ? currentAlert.dismissTime : dismissTime,
          alert.dismissTime,
        );

        if ( dismissTimeout ) {
          clearTimeout( dismissTimeout as number );
        }

        setDismissTimeout( setTimeout( onDismiss, Math.abs( nextDismissTime - Date.now() ) ) );
      }

      setAlerts( ( previousAlerts ) => [...previousAlerts, alert] );
    },
    [defaultTimeout, dismissTimeout, alerts],
  );

  const location = useLocation();
  useEffect( () => {
    setAlerts( [] );
  }, [location] );

  const hideAlert = ( alertToHide: AlertObject ) => {
    setAlerts( alerts.filter( ( alert ) => !isEqual( alert, alertToHide ) ) );
  };

  // export addAlert call to rest of page
  useEffect( () => {
    setContext( ( context: AlertContextInterface ) => ( {
      ...context,
      addAlert,
    } ) );
  }, [setContext, addAlert] );

  // display any alerts set on a previous page
  useEffect(
    () => {
      const params = retrieveFlashAlert();
      if ( params && params[0] && params[1] ) {
        addAlert( params[0], params[1], params[2] ?? true );
        deleteFlashAlert();
      }
    },
    // TODO: Write unit tests, then see if satisfying react-hooks/exhaustive-deps breaks this or not.
    [], // eslint-disable-line react-hooks/exhaustive-deps
  );

  if ( alerts.length === 0 ) {
    return null;
  }

  return (
    <div className={ BEM.$( bem ) }>
      { alerts.map( ( alert, key ) => (
        <div key={ key } className={ BEM.$( bem.el( 'alertEntry' ) ) }>
          <Alert show={ true } variant={ alert.variant } dismissible onClose={ () => hideAlert( alert ) }>
            { alert.message.message }
            { alert.message.error && (
              <>
                { ' ' }
                (
                <span
                  onClick={ () => {
                    delete alert.dismissTime;
                    setShowMoreDialog( alert );
                  } }
                  className={ BEM.$( bem.el( 'detailsLink' ) ) }
                >
                  details
                </span>
                )
              </>
            ) }
            { alert.message.link && (
              <a href={ alert.message.link } target="_blank" rel="noopener noreferrer" className={ BEM.$( bem.el( 'link' ) ) }>
                <FontAwesomeIcon icon={ faArrowUpRightFromSquare } />
              </a>
            ) }
          </Alert>
        </div>
      ) ) }
      <ConfirmDialog show={ !!showMoreDialog } onConfirm={ () => setShowMoreDialog( null ) } title="Error Details">
        <div>
          <ErrorDisplay error={ showMoreDialog?.message.error } />
        </div>
      </ConfirmDialog>
    </div>
  );
};

export const DisplayModal = ( { setContext }: { setContext: SetContext } ) => {
  const [modal, setModal] = useState( null );
  const location = useLocation();

  const onHide = useCallback( () => {
    setModal( null );
  }, [] );

  // clear out the modal when the location changes
  useEffect( () => {
    setModal( null );
  }, [location] );

  const showModal = useCallback(
    ( render ) => {
      setModal( render( onHide ) );
    },
    [], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // export showModal call to rest of page
  useEffect( () => {
    setContext( ( context: AlertContextInterface ) => ( {
      ...context,
      showModal,
    } ) );
  }, [setContext, showModal] );

  return modal;
};

export interface AlertProviderProps {
  children: ReactNode;
  defaultTimeout?: number;
}

export const AlertProvider = ( { children, defaultTimeout }: AlertProviderProps ) => {
  // Use the provided defaultTimeout or fallback to DEFAULT_TIMEOUT (3000)
  const timeout = defaultTimeout ?? DEFAULT_TIMEOUT;
  // add alert
  const [context, setContext] = useState<AlertContextInterface>( {
    addAlert: null,
    showModal: null,
  } );

  return (
    <AlertContext.Provider value={ context }>
      {
        context.addAlert &&
          context.showModal &&
          children /* only render children once DisplayAlert has registered callbacks */
      }
      <DisplayAlert setContext={ setContext } defaultTimeout={ timeout } />
      <DisplayModal setContext={ setContext } />
    </AlertContext.Provider>
  );
};
